/**
 * `useChatStream` — the SSE consumer hook: drives one chat turn and assembles the
 * `StreamingTurn` view-model from the event stream (editor-chat.md §2.3; ADR 007).
 *
 * ADR 007: the frontend chat surface is a **streaming SSE consumer** of the backend `/chat`
 * endpoint — not the loop driver. This hook is that consumer. It:
 *  - opens the `/chat` SSE stream via `streamChatTurn`;
 *  - folds each `ChatStreamEvent` into a `StreamingTurn` view-model — `text-delta` appends to
 *    the bubble text, `tool-call` / `tool-result` build the inline "the agent did X"
 *    affordances (correlated by `callId`), `usage` sets the telemetry, `done` / `error` set
 *    the terminal phase;
 *  - guarantees the turn always *resolves* — if the stream throws before a terminal event
 *    (an auth/transport failure), the hook synthesizes a `model_unavailable` error so the chat
 *    surface always reaches the graceful-failure state (conventions.md §3 — surface, never
 *    drop; editor-chat.md §1.3 — graceful failure is a designed state).
 *
 * The hook owns the *in-flight* turn only. Completed turns are the session's persisted
 * messages — the chat surface refetches the session after a turn (or appends optimistically);
 * this hook does not own session history.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { getLogger } from '@/api';
import { streamChatTurn } from './chatApi';
import type {
  ChatStreamEvent,
  ChatToolCallView,
  ChatTurnRequest,
  StreamingTurn,
} from './chatTypes';

const log = getLogger('chat.stream');

/** The initial `StreamingTurn` — an empty turn before any event has arrived. */
const INITIAL_TURN: StreamingTurn = {
  phase: 'streaming',
  text: '',
  toolCalls: [],
  usage: null,
  error: null,
  messageId: null,
  multipassPhase: null,
  multipassPhaseLabel: null,
  currentRound: null,
  maxRounds: null,
  approxInputTokens: null,
  compressedResults: 0,
};

/** What `useChatStream` exposes to the chat surface. */
export interface UseChatStreamResult {
  /** The in-flight (or just-finished) turn's view-model — `null` when no turn has run yet. */
  turn: StreamingTurn | null;
  /** True while a turn's stream is open. */
  isStreaming: boolean;
  /** Start a turn — opens the SSE stream and folds its events into `turn`. */
  sendTurn: (request: ChatTurnRequest) => Promise<void>;
  /**
   * Abort the in-flight turn's stream, if any (perf D5) — the surface calls this on session
   * switch / new-chat / clear-history so the old turn's events can't resurrect its bubble in
   * the new view. Synchronously frees the send slot and drops `isStreaming`; every later
   * event / outcome of the aborted turn is discarded (the generation guard). No-op when idle.
   * Server-side the abort is cheap: the backend persists the user message at turn start and
   * cancels the model call on disconnect.
   */
  cancelTurn: () => void;
  /** Clear the current turn view-model — the surface calls this when starting a fresh message. */
  reset: () => void;
}

/**
 * Fold one `ChatStreamEvent` into the running `StreamingTurn` view-model — a pure reducer.
 *
 * Exported so it is unit-testable in isolation: feed it a scripted event sequence and assert
 * the resulting view-model, with no SSE / network involved.
 */
export function reduceTurn(turn: StreamingTurn, event: ChatStreamEvent): StreamingTurn {
  switch (event.kind) {
    case 'phase':
      // Phase boundary. Three cases clear the bubble text:
      //   * 'refining' — multipass single-stage path: the draft pass produced internal
      //     text; the refine pass produces the visible answer, so we wipe any leak.
      //   * 'synthesizing' — two-stage pipeline (#102 PR 3): the researcher pass is
      //     done (it suppresses text deltas anyway, but defensively); the writer's
      //     bubble starts fresh as text-delta events flow in.
      //   * 'polishing' — three-stage pipeline (#102 follow-up): the writer's draft
      //     just streamed in; the formatter pass now re-formats it with proper
      //     markdown structure. Clearing makes the formatter's streamed output
      //     replace (rather than concatenate with) the writer's draft.
      // The same clear-on-phase contract keeps the UI bubble fresh as the model that's
      // ACTUALLY producing visible text takes over.
      return {
        ...turn,
        multipassPhase: event.phase,
        multipassPhaseLabel: event.label,
        ...(event.phase === 'refining' ||
        event.phase === 'synthesizing' ||
        event.phase === 'polishing'
          ? { text: '' }
          : {}),
      };

    case 'text-delta':
      // Append the delta to the streaming bubble's text.
      return { ...turn, text: turn.text + event.text };

    case 'model-call':
      // A new model round is about to run — the honest "consulting the model" signal
      // (editor-chat.md §2.3 transparency). Record the round progress + the coarse context
      // size for the live status / activity timeline, and ACCUMULATE the condensed-results
      // count across the turn (each round condenses a little more older tool-result text).
      return {
        ...turn,
        currentRound: event.iteration,
        maxRounds: event.maxIterations,
        approxInputTokens: event.approxInputTokens,
        compressedResults: turn.compressedResults + event.compressedResults,
      };

    case 'tool-call': {
      // A new tool call — append a `running` affordance, correlated by callId.
      const toolCall: ChatToolCallView = {
        callId: event.callId,
        toolName: event.toolName,
        argsSummary: event.argsSummary,
        status: 'running',
        resultSummary: null,
        innerTools: null, // populated on tool-result if the tool was a workflow
        resultTitles: null, // populated on tool-result if the tool returned a note list
      };
      return { ...turn, toolCalls: [...turn.toolCalls, toolCall] };
    }

    case 'tool-result':
      // Resolve the matching tool call (by callId) — `error` is the recoverable path: the
      // agent saw the error and is continuing (editor-chat.md §2.3). For workflow tools,
      // `event.innerTools` carries the composed primitives — we attach them to the view
      // so the affordance can render the subtitle ("workflow → primitive1, primitive2").
      // `event.resultTitles` carries up to 3 note titles the tool returned — the "what came
      // back" snippet the affordance shows so the user sees the data flowing.
      return {
        ...turn,
        toolCalls: turn.toolCalls.map((tc) =>
          tc.callId === event.callId
            ? {
                ...tc,
                status: event.status,
                resultSummary: event.resultSummary,
                innerTools: event.innerTools,
                resultTitles: event.resultTitles,
              }
            : tc,
        ),
      };

    case 'usage':
      // The turn-aggregate token/cost telemetry — emitted once at turn end (REVIEW M5).
      return {
        ...turn,
        usage: { usage: event.usage, costUsd: event.costUsd, model: event.model },
      };

    case 'done':
      // The terminal success event — the turn completed normally.
      return { ...turn, phase: 'done', messageId: event.messageId };

    case 'error':
      // The terminal failure / bounded-state event. The partial `text` is kept (an
      // `iteration_limit` / degradation keeps what was streamed — editor-chat.md §2.3).
      return {
        ...turn,
        phase: 'error',
        error: {
          category: event.category,
          message: event.message,
          correlationId: event.correlationId,
        },
        messageId: event.messageId,
      };
  }
}

/**
 * Drive one chat turn at a time, exposing the in-flight `StreamingTurn` view-model.
 *
 * `sendTurn` opens the SSE stream and folds events into `turn` as they arrive — React
 * re-renders on each fold, so the chat surface sees the bubble grow and the tool affordances
 * resolve live. The hook guards against a torn-down component: a stream that is still
 * producing events after unmount is abandoned (the ref guard), it does not setState into a
 * dead component.
 */
export function useChatStream(): UseChatStreamResult {
  const [turn, setTurn] = useState<StreamingTurn | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  // Guards setState-after-unmount and double-send: a turn in flight has `activeRef.current`
  // set to its turn id; a new send while one is active is ignored.
  const activeRef = useRef<string | null>(null);
  // The in-flight turn's AbortController (perf D5) — created synchronously in `sendTurn`
  // (before any await, so a cancel during the connection-opening await still aborts the fetch).
  const controllerRef = useRef<AbortController | null>(null);
  // The turn generation — bumped by `cancelTurn` / unmount. Each `sendTurn` captures the
  // generation at start; any mismatch later means "this turn was cancelled": stop folding
  // events (no `setTurn` resurrection — the stale-closure guard), skip the terminal-error
  // synthesis, and skip the finally-cleanup (a NEWER turn may own the slot by then).
  const generationRef = useRef(0);
  // `mountedRef` is flipped false on unmount so a still-producing stream abandons rather than
  // setState-ing into a dead component (the chat surface is long-lived, but a route change
  // away mid-turn must be clean).
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      // D5: tear the stream down on unmount — don't leave the fetch running against a dead
      // component. The generation bump makes the in-flight turn discard every later outcome.
      generationRef.current += 1;
      controllerRef.current?.abort();
      controllerRef.current = null;
      activeRef.current = null;
    };
  }, []);

  const reset = useCallback(() => {
    setTurn(null);
  }, []);

  const cancelTurn = useCallback(() => {
    if (activeRef.current === null) return; // nothing in flight
    log.info('chat_turn_cancelled', {});
    // Invalidate the in-flight turn FIRST (stale-closure guard), then abort its stream. Free
    // the slot + drop `isStreaming` synchronously so the composer unlocks and a new send can
    // start immediately — the old turn's finally-cleanup sees the generation mismatch and
    // leaves the new turn's state alone.
    generationRef.current += 1;
    activeRef.current = null;
    const controller = controllerRef.current;
    controllerRef.current = null;
    controller?.abort(); // aborts the fetch + cancels the SSE reader (chatApi D5)
    setIsStreaming(false);
  }, []);

  const sendTurn = useCallback(async (request: ChatTurnRequest): Promise<void> => {
    if (activeRef.current !== null) {
      // A turn is already streaming — ignore the double-send. The surface disables the
      // composer while `isStreaming`, so this is defence-in-depth.
      log.warn('chat_send_while_streaming', {});
      return;
    }
    // Claim the slot SYNCHRONOUSLY, before any `await`. Critical: the composer's submit /
    // isStreaming guards are React-state-gated (async), so a rapid double-action (double
    // click, key-repeat, Enter+submit) can call sendTurn twice before the first re-render
    // disables the composer. If we set the guard only AFTER `await streamChatTurn(...)`, both
    // calls pass the `activeRef.current !== null` check → two backend turns → the duplicate
    // answers. Setting a sentinel here makes the second call bail immediately. (2026-05-22)
    activeRef.current = 'pending';
    // Capture this turn's generation + mint its AbortController SYNCHRONOUSLY (D5) — a
    // cancel that lands while `streamChatTurn` is still opening the connection must both
    // abort the fetch (the controller) and invalidate this closure (the generation).
    const gen = generationRef.current;
    const controller = new AbortController();
    controllerRef.current = controller;
    // Start a fresh turn view-model.
    let working: StreamingTurn = { ...INITIAL_TURN };
    setTurn(working);
    setIsStreaming(true);

    try {
      const handle = await streamChatTurn(request, { signal: controller.signal });
      if (generationRef.current !== gen) return; // cancelled while the stream was opening
      activeRef.current = handle.turnId;
      for await (const event of handle.events) {
        // Component torn down or turn cancelled (session switch — D5): abandon the stream.
        // The generation guard is what stops a cancelled turn's late events from
        // resurrecting its bubble in the NEW session's view via setTurn.
        if (!mountedRef.current || generationRef.current !== gen) break;
        working = reduceTurn(working, event);
        setTurn(working);
      }
      // A deliberately-cancelled stream can end cleanly (reader.cancel) — its "no terminal
      // event" is expected, not a truncation. Discard silently.
      if (generationRef.current !== gen) return;
      // Defensive: the backend always ends a turn with a terminal event, but if the stream
      // closed without one (a truncated connection), synthesize a terminal error so the turn
      // resolves and the surface leaves the streaming state (conventions.md §3, §7).
      if (working.phase === 'streaming') {
        log.warn('chat_stream_closed_without_terminal', { turnId: handle.turnId });
        working = reduceTurn(working, {
          kind: 'error',
          category: 'model_unavailable',
          message: 'The chat connection closed unexpectedly. Try again shortly.',
          correlationId: handle.turnId,
          messageId: null,
        });
        setTurn(working);
      }
    } catch (err) {
      if (generationRef.current !== gen || !mountedRef.current) {
        // A deliberate cancel (session switch / unmount) aborts the fetch — the resulting
        // AbortError is the expected wind-down, not a failure to surface (D5).
        log.debug('chat_turn_stream_aborted', { error: String(err) });
        return;
      }
      // The stream never opened (auth/transport failure) or stalled mid-turn (the D6
      // watchdog's ChatStreamStalledError — its message carries the stall wording) —
      // synthesize the terminal error so the surface reaches the graceful-failure state.
      // The chat is degraded; every other surface and the editor are unaffected
      // (editor-chat.md §1.3).
      log.error('chat_turn_stream_error', { error: String(err) });
      working = reduceTurn(working, {
        kind: 'error',
        category: 'model_unavailable',
        message:
          err instanceof Error
            ? err.message
            : 'The chat model is currently unavailable.',
        correlationId: 'n/a',
        messageId: null,
      });
      setTurn(working);
    } finally {
      // Only clean up if this turn still owns the slot — after a cancel, a NEWER turn may
      // already be streaming, and clobbering activeRef/isStreaming here would corrupt it.
      if (generationRef.current === gen) {
        activeRef.current = null;
        controllerRef.current = null;
        if (mountedRef.current) setIsStreaming(false);
      }
    }
  }, []);

  return { turn, isStreaming, sendTurn, cancelTurn, reset };
}
